import { Link, type LinkProps } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { Product, Variant } from '@nucleus-commerce/api-client/admin';
import type { Money } from '@nucleus-commerce/core';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Card } from '@nucleus-commerce/ui/components/card';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { MediaThumb, useMedia } from '@/features/media';

import { ProductStatusBadge } from './product-status-badge';

const ALL = '__all__';

function Thumb({ mediaId, className }: { mediaId: string | null; className: string }) {
  const { data } = useMedia(mediaId);
  return <MediaThumb media={data} className={cn('shrink-0 rounded-md border', className)} />;
}

/** What a rail row shows under the variant's name; a null part is left out. */
export interface VariantRailMeta {
  price: Money | null;
  /** Null for an untracked variant, or for someone who may not read stock. */
  stock: number | null;
}

/** The rail's top card, for a store page whose title, image and status differ from the master's. */
export interface VariantRailHeading {
  title: string;
  mediaId: string | null;
  badge: ReactNode;
}

export interface VariantEditorRailProps {
  product: Product;
  variants: readonly Variant[];
  currentId: string;
  linkTo: (variantId: string) => LinkProps;
  /** The product page this variant belongs to. */
  productLink: LinkProps;
  meta: (variant: Variant) => VariantRailMeta;
  heading?: VariantRailHeading | undefined;
}

/**
 * The variant page's left column: the product, then every sibling variant to switch to without
 * going back through the product page. A search and one filter per option narrow long lists.
 */
export function VariantEditorRail({
  product,
  variants,
  currentId,
  linkTo,
  productLink,
  meta,
  heading,
}: VariantEditorRailProps) {
  const { t } = useTranslation('products');
  const format = useFormat();
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Record<string, string>>({});
  const text = query.trim().toLowerCase();
  const narrowed = text !== '' || Object.keys(picked).length > 0;
  const shown = variants.filter(
    (variant) =>
      (text === '' ||
        variant.title.toLowerCase().includes(text) ||
        (variant.sku ?? '').toLowerCase().includes(text)) &&
      Object.entries(picked).every(([key, value]) => variant.optionValues[key] === value),
  );

  return (
    <div className="flex flex-col gap-4">
      <Card className="py-0">
        <Link {...productLink} className="flex items-center gap-3 p-4">
          <Thumb mediaId={heading?.mediaId ?? product.featuredMediaId} className="size-12" />
          <span className="grid min-w-0 gap-1">
            <span className="truncate text-[13px] font-semibold">
              {heading?.title ?? product.title}
            </span>
            <span className="flex flex-wrap items-center gap-2">
              {heading?.badge ?? <ProductStatusBadge status={product.status} className="w-fit" />}
              <span className="text-muted-foreground text-xs">
                {t('variants.count', { count: variants.length })}
              </span>
            </span>
          </span>
        </Link>
      </Card>

      <Card className="gap-0 overflow-hidden py-0">
        <div className="flex flex-col gap-2 border-b p-3">
          <div className="relative">
            <Search
              aria-hidden
              className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
            />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('variantPage.searchVariants')}
              aria-label={t('variantPage.searchVariants')}
              className="h-8 pl-8 text-sm"
            />
          </div>
          {product.options.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {product.options.map((option) => (
                <Select
                  key={option.key}
                  value={picked[option.key] ?? ALL}
                  onValueChange={(value) =>
                    setPicked((current) => ({
                      ...Object.fromEntries(
                        Object.entries(current).filter(([key]) => key !== option.key),
                      ),
                      ...(value === ALL ? {} : { [option.key]: value }),
                    }))
                  }
                >
                  <SelectTrigger
                    size="sm"
                    aria-label={t('variantPage.filterBy', { option: option.name })}
                    className="h-7 rounded-full text-xs"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>{option.name}</SelectItem>
                    {option.values.map((value) => (
                      <SelectItem key={value.id} value={value.value}>
                        {value.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ))}
            </div>
          ) : null}
          {narrowed ? (
            <span className="text-muted-foreground text-xs">
              {t('variants.count', { count: shown.length })}
            </span>
          ) : null}
        </div>
        <nav aria-label={t('variants.title')} className="max-h-[60vh] overflow-y-auto">
          {shown.map((variant) => {
            const current = variant.id === currentId;
            const { price, stock } = meta(variant);
            const parts = [
              ...(price ? [format.money(price)] : []),
              ...(stock === null ? [] : [t('variantPage2.inStock', { count: stock })]),
            ];
            return (
              <Link
                key={variant.id}
                {...linkTo(variant.id)}
                aria-current={current ? 'page' : undefined}
                className={cn(
                  'hover:bg-muted/60 flex items-center gap-2.5 border-t px-3 py-2 first:border-t-0',
                  current && 'bg-primary-tint hover:bg-primary-tint',
                )}
              >
                <Thumb mediaId={variant.mediaId} className="size-8" />
                <span className="grid min-w-0">
                  <span className="truncate text-[13px] font-medium">{variant.title}</span>
                  {parts.length > 0 ? (
                    <span className="text-muted-foreground truncate text-xs tabular-nums">
                      {parts.join(' · ')}
                    </span>
                  ) : null}
                </span>
              </Link>
            );
          })}
        </nav>
      </Card>
    </div>
  );
}
